import React, { useState } from "react";
import { CalendarPlus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field } from "./LeadForm";

const ACTIONS = ["Call back", "Send quotation", "Site visit", "Demo", "Negotiation",
  "Awaiting approval", "Payment follow-up", "Close deal"];

const today = () => new Date().toISOString().slice(0, 10);
const addDays = (n) => new Date(Date.now() + n * 864e5).toISOString().slice(0, 10);

/** Quick "what just happened + what next" entry, straight from the lead list. */
export const RecordActivityDialog = ({ lead, open, onOpenChange, onSaved }) => {
  const [form, setForm] = useState({
    date: today(), time: "10:00", remarks: "", next_action: "Call back", next_date: addDays(3),
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.remarks.trim()) return toast.error("Write what happened in this activity.");
    setBusy(true);
    try {
      await api.post("/followups", { lead_id: lead.id, status: "Completed", ...form });
      toast.success(form.next_date
        ? `Activity recorded \u00b7 next ${form.next_action.toLowerCase()} on ${form.next_date}`
        : "Activity recorded");
      setForm({ ...form, remarks: "" });
      onSaved?.();
      onOpenChange(false);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Record activity</DialogTitle>
          <DialogDescription>
            {lead?.customer_name}{lead?.company ? ` \u00b7 ${lead.company}` : ""} &middot; {lead?.lead_no}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Date">
              <Input type="date" value={form.date} data-testid="ra-date"
                onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </Field>
            <Field label="Time">
              <Input type="time" value={form.time}
                onChange={(e) => setForm({ ...form, time: e.target.value })} />
            </Field>
          </div>

          <Field label="What happened?" required>
            <Textarea rows={3} value={form.remarks} data-testid="ra-remarks"
              onChange={(e) => setForm({ ...form, remarks: e.target.value })}
              placeholder="Spoke to the purchase head, they want a demo before the order..." />
          </Field>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Next activity">
              <Select value={form.next_action} onValueChange={(v) => setForm({ ...form, next_action: v })}>
                <SelectTrigger data-testid="ra-next-action"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ACTIONS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Follow-up date" hint="Clear it if nothing further is needed.">
              <Input type="date" value={form.next_date} data-testid="ra-next-date"
                onChange={(e) => setForm({ ...form, next_date: e.target.value })} />
            </Field>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="ra-save">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarPlus className="h-4 w-4" />}
              Save activity
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
